import React, { useState } from "react";
import Label from "../Common/Label";
import RowContainer from "../Common/RowContainer";
import Container from "../Common/Container";
import ColumnContainer from "../Common/ColumnContainer";
import styled from "styled-components";
import formatDate from "../DateFormat";

interface AnswerField {
  textfieldsChallengeParticlesId: number;
  userAnswer: string;
  keyword: string;
  status?: "approved" | "rejected" | null;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  active: boolean;
  fullName: string;
}

interface TextFieldsChallenge {
  id: number;
  question: string;
  points: number;
  active: boolean;
}

interface Theme {
  id: number;
  name: string;
  lessonId: number;
  order: number;
}

interface Lesson {
  id: number;
  level: string;
  title: string;
}

interface UserChallenge {
  id: number;
  status: string;
  attempts: number;
  completed: boolean;
}

interface TextFieldReportItem {
  id: number;
  textfieldsChallengeId: number;
  userId: number;
  themeId: number;
  userChallengeId: number;
  lessonId: number;
  data: AnswerField[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
  user: User;
  textfieldsChallenge: TextFieldsChallenge;
  theme: Theme;
  lesson: Lesson;
  userChallenge: UserChallenge;
}

interface TextFieldReportProps {
  report: TextFieldReportItem;
  onApprove: (reportId: number, particleId: number) => void;
  onReject: (reportId: number, particleId: number) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

const NewRowContainer = styled(RowContainer)`
  overflow: hidden;
  min-width: 0;
`;

const NewLabel = styled(Label)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  display: inline-block;
  width: 100%;
`;

const ExpandIcon = styled.span<{ isExpanded: boolean }>`
  cursor: pointer;
  user-select: none;
  transition: transform 0.2s ease;
  transform: ${({ isExpanded }) => (isExpanded ? "rotate(180deg)" : "rotate(0deg)")};
  display: inline-block;
  font-size: 14px;
  color: #666;
  font-weight: bold;
`;

const ExpandedContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${({ isExpanded }) => (isExpanded ? "2000px" : "0")};
  opacity: ${({ isExpanded }) => (isExpanded ? "1" : "0")};
  overflow: hidden;
  transition: all 0.3s ease;
  background: #f9f9f9;
  border-radius: 8px;
  margin-top: ${({ isExpanded }) => (isExpanded ? "8px" : "0")};
  width: 100%;
`;

const SubTable = styled.div`
  width: 100%;
  padding: 12px;
`;

const SubTableRow = styled(RowContainer)`
  padding: 10px 12px;
  border-bottom: 1px solid #e8e8e8;
  background: #fff;
  width: 100%;
  
  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }
`;

const TextFieldReport: React.FC<TextFieldReportProps> = ({
  report,
  onApprove,
  onReject,
  isExpanded,
  onToggleExpanded,
}) => {
  const toggleExpanded = () => {
    onToggleExpanded();
  };

  // Helper function to truncate text
  const truncateText = (text: string, maxLength: number = 15): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Create truncated display text
  const truncatedUserInfo = `${truncateText(report.user.fullName, 15)} | ${truncateText(report.theme.name, 15)} | ${truncateText(report.lesson.title, 10)} | ${report.lesson.level}`;
  const fullUserInfo = `${report.user.fullName} | ${report.theme.name} | ${report.lesson.title} | ${report.lesson.level}`;

  return (
    <ColumnContainer style={{ width: "100%" }}>
      <Container
        bgColor="#fff"
        borderRadius="10px"
        height="50px"
        alignItems="center"
        display="flex"
        width="100%"
        className="user-data user-container-hover"
        style={{ cursor: "pointer" }}
        onClick={toggleExpanded}
      >
        <RowContainer
          justifyContent="space-between"
          alignItem="center"
          borderRadius="12px"
          margin="10px"
          width="100%"
        >
          {/* Expand Icon */}
          <RowContainer flex="1" alignItem="center">
            <ExpandIcon isExpanded={isExpanded}>
              ▼
            </ExpandIcon>
          </RowContainer>

          {/* Question */}
          <NewRowContainer flex="11" gap="0px">
            <NewLabel fontSize="16px" fontWeight={400} title={report.textfieldsChallenge.question}>
              {truncateText(report.textfieldsChallenge.question, 50)}
            </NewLabel>
          </NewRowContainer>

          {/* User | Theme | Lesson | Level */}
          <NewRowContainer flex="8" gap="0px">
            <NewLabel
              fontSize="14px"
              fontWeight={400}
              color="#666"
              title={fullUserInfo}
            >
              {truncatedUserInfo}
            </NewLabel>
          </NewRowContainer>

          {/* Created At */}
          <NewRowContainer flex="4" gap="0px">
            <NewLabel fontSize="16px" fontWeight={400}>
              {formatDate(report.createdAt)}
            </NewLabel>
          </NewRowContainer>
        </RowContainer>
      </Container>

      {/* Expanded Section with Answer Fields */}
      <ExpandedContent isExpanded={isExpanded}>
        <SubTable>
          {/* Header Row */}
          <SubTableRow
            justifyContent="space-between"
            style={{
              background: "#f0f0f0",
              fontWeight: 600,
              borderRadius: "8px 8px 0 0",
            }}
          >
            <RowContainer flex="2">
              <Label fontSize="14px" fontWeight={600} color="#555">
                Answer Field
              </Label>
            </RowContainer>
            <RowContainer flex="4">
              <Label fontSize="14px" fontWeight={600} color="#555">
                Keyword (Original)
              </Label>
            </RowContainer>
            <RowContainer flex="4">
              <Label fontSize="14px" fontWeight={600} color="#555">
                User Answer
              </Label>
            </RowContainer>
            <RowContainer flex="3">
              <Label fontSize="14px" fontWeight={600} color="#555">
                Actions
              </Label>
            </RowContainer>
          </SubTableRow>

          {/* Data Rows */}
          {report.data.map((field, index) => (
            <SubTableRow key={field.textfieldsChallengeParticlesId} justifyContent="space-between">
              <RowContainer flex="2">
                <Label fontSize="14px" fontWeight={500}>
                  Field {index + 1}
                </Label>
              </RowContainer>
              <RowContainer flex="4">
                <Label fontSize="14px" color="#333">
                  {field.keyword}
                </Label>
              </RowContainer>
              <RowContainer flex="4">
                <Label fontSize="14px" color="#333" fontWeight={500}>
                  {field.userAnswer}
                </Label>
              </RowContainer>
              <RowContainer flex="3">
                {field.status ? (
                  // Show status badge if already approved/rejected
                  <div style={{
                    padding: "6px 16px",
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: 600,
                    backgroundColor: field.status === "approved" ? "#d4edda" : "#f8d7da",
                    color: field.status === "approved" ? "#155724" : "#721c24",
                    textTransform: "capitalize"
                  }}>
                    {field.status === "approved" ? "✓ Approved" : "✕ Rejected"}
                  </div>
                ) : (
                  // Show action buttons if not yet processed
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{
                        backgroundColor: "#4CAF50",
                        color: "#fff",
                        borderRadius: "6px",
                        fontSize: "13px",
                        padding: "4px 12px",
                        border: "none",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onApprove(report.id, field.textfieldsChallengeParticlesId);
                      }}
                    >
                      ✓ Approve
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{
                        backgroundColor: "#f44336",
                        color: "#fff",
                        borderRadius: "6px",
                        fontSize: "13px",
                        padding: "4px 12px",
                        border: "none",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onReject(report.id, field.textfieldsChallengeParticlesId);
                      }}
                    >
                      ✕ Reject
                    </button>
                  </div>
                )}
              </RowContainer>
            </SubTableRow>
          ))}
        </SubTable>
      </ExpandedContent>
    </ColumnContainer>
  );
};

export default TextFieldReport;
